import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  HStack,
  Input,
  InputGroup,
  Portal,
  Stack,
} from '@chakra-ui/react';
import type { UserType } from '@customTypes/user';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';
import useGetUsersList from '@hooks/useGetUsersList';
import useGetAllUsersList from '@hooks/useGetAllUsersList';
import useGetAllPortfolioList from '@hooks/useGetAllPortfolioList';
import UserSelect from '@components/UserSelect';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';
import DualListPanel, { type DualListItem } from '@components/DualListPanel';
import StepIndicator from '@components/StepIndicator';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
};

const STEPS = [
  { id: 1, label: 'Equipe' },
  { id: 2, label: 'Membros' },
  { id: 3, label: 'Portfólios' },
];

const AddDialog = ({ handleClose, afterSubmit }: Props) => {
  const { getUsers, users } = useGetUsersList();
  const { getAllUsers, users: allUsers, loading: allUsersLoading } = useGetAllUsersList();
  const { getPortfolios, portfolios, loading: portfoliosLoading } = useGetAllPortfolioList();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);

  const [name, setName] = useState('');
  const [selectedTeamLead, setSelectedTeamLead] = useState<UserType | null>(null);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [portfolioIds, setPortfolioIds] = useState<string[]>([]);

  useEffect(() => {
    getUsers({});
    getAllUsers();
    getPortfolios();
  }, [getUsers, getAllUsers, getPortfolios]);

  const memberItems = useMemo<DualListItem[]>(
    () => allUsers.map((u) => ({ id: u.id, label: u.name })),
    [allUsers]
  );

  const portfolioItems = useMemo<DualListItem[]>(
    () => portfolios.map((p) => ({ id: p.id, label: p.name })),
    [portfolios]
  );

  const canAdvanceStep1 = name.trim().length >= 3 && !!selectedTeamLead;

  const handleNext = () => setStep((prev) => Math.min(prev + 1, STEPS.length));
  const handleBack = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    if (!selectedTeamLead) {
      setStep(1);
      setErrors({ team_lead_id: ['Selecione um líder de equipe'] });
      return;
    }

    setLoading(true);

    const params = {
      name: capitalizeFirstLetter(name.trim()),
      team_lead_id: selectedTeamLead.id,
      members_ids: memberIds,
      portfolios_ids: portfolioIds,
    };

    try {
      const response = await api.post('/teams', { ...params });

      if (response?.status === 204 || response?.status === 201) {
        toast.success('Equipe criada com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
        setStep(1);
      }
    } catch (error) {
      toast.error('Erro ao criar a equipe. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to create team'), {
        error,
        params,
      });
    } finally {
      setLoading(false);
    };
  };

  const isLastStep = step === STEPS.length;

  return (
    <Dialog.Root
      size="xl"
      placement="center"
      open
      onOpenChange={handleClose}
      closeOnInteractOutside={false}
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="grayscale.fg"
            maxW={step === 1 ? undefined : '1100px'}
            overflow="hidden"
            display="flex"
            flexDir="column"
            maxH={{ base: '100dvh', lg: 'calc(100dvh - 2rem)' }}
          >
            <Dialog.Header>
              <Stack gap="0" w="full">
                <Dialog.Title>Nova equipe</Dialog.Title>
                <Box marginTop={4}>
                  <StepIndicator steps={STEPS} current={step} />
                </Box>
              </Stack>
            </Dialog.Header>

            <Dialog.Body
              flex="1"
              display="flex"
              flexDir="column"
              overflow={{ base: 'auto', lg: step === 1 ? 'auto' : 'hidden' }}
              minH="0"
            >
              {step === 1 && (
                <Stack gap="2">
                  <Field.Root invalid={!!errors?.name}>
                    <Field.Label>Nome</Field.Label>
                    <InputGroup width="full" gap={0}>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Digite aqui..."
                        minLength={3}
                        variant="subtle"
                        required
                      />
                    </InputGroup>
                    {errors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Box>
                    <UserSelect
                      label="Líder"
                      errors={errors}
                      users={users}
                      onSearch={async (text) => await getUsers({ name: text })}
                      onChange={(user) => setSelectedTeamLead(user)}
                      value={selectedTeamLead}
                      showSelectedValue
                    />
                    {errors?.team_lead_id?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>
                </Stack>
              )}

              {step === 2 && (
                allUsersLoading ? (
                  <Loading />
                ) : (
                  <DualListPanel
                    items={memberItems}
                    selectedIds={memberIds}
                    onChange={setMemberIds}
                    labels={{
                      defaultGroup: 'Membros',
                      availableSubtitle: 'Selecione um ou vários membros',
                      selectedSubtitle: 'Remova rapidamente o que não pertence a essa equipe',
                      emptyAvailableTitle: 'Nenhum membro encontrado',
                      emptyAvailableDescription: 'Tente outro termo de busca ou revise os filtros aplicados.',
                      emptySelectedTitle: 'Nenhum membro selecionado',
                      emptySelectedDescription: 'Escolha membros da coluna à esquerda para adicioná-los à equipe.',
                    }}
                  />
                )
              )}

              {step === 3 && (
                portfoliosLoading ? (
                  <Loading />
                ) : (
                  <DualListPanel
                    items={portfolioItems}
                    selectedIds={portfolioIds}
                    onChange={setPortfolioIds}
                    labels={{
                      defaultGroup: 'Portfólios',
                      availableSubtitle: 'Selecione um ou vários portfólios',
                      selectedSubtitle: 'Remova rapidamente o que não pertence a essa equipe',
                      emptyAvailableTitle: 'Nenhum portfólio encontrado',
                      emptyAvailableDescription: 'Tente outro termo de busca ou revise os filtros aplicados.',
                      emptySelectedTitle: 'Nenhum portfólio selecionado',
                      emptySelectedDescription: 'Escolha itens da coluna à esquerda para adicioná-los à equipe.',
                    }}
                  />
                )
              )}

              {errors?.general && (
                <Flex direction="column" mt={4}>
                  {errors.general.map((error, index) => (
                    <ErrorLabel key={index} text={error} />
                  ))}
                </Flex>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justify="space-between" w="full" gap="3">
                <Button variant="outline" onClick={handleClose} disabled={loading}>
                  Cancelar
                </Button>
                <HStack>
                  {step > 1 && (
                    <Button variant="ghost" onClick={handleBack} disabled={loading}>
                      Voltar
                    </Button>
                  )}
                  {!isLastStep ? (
                    <Button
                      bg="green.700"
                      color="white"
                      onClick={handleNext}
                      disabled={step === 1 && !canAdvanceStep1}
                    >
                      Próximo
                    </Button>
                  ) : (
                    <Button bg="green.700" color="white" onClick={handleSubmit} loading={loading}>
                      Salvar
                    </Button>
                  )}
                </HStack>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AddDialog;
